import { metricKeys, SESSION_JOURNEY_VERSION, type AccountingFrame, type GameEvent, type GameStateSnapshot, type LiveRateSnapshot, type RankSubmissionDraft, type SessionAggregate, type SessionIngestResult, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { EfficiencySegmentTracker, RANK_PRESET_ID } from './efficiency-segment-tracker'
import { SessionExpCounter } from './session-exp-counter'
import { SessionExpRateTracker } from './session-exp-rate'
import { SessionExpReadout } from './session-exp-readout'
import { SessionJourneyRecorder } from './session-journey'
import { TrustedMetricLedger } from './trusted-metric-ledger'
import type { PerformanceTier } from './performance-settings'
import type { RankSourceRestriction } from './rank-capture'

const emptyCoverage = () => Object.fromEntries(metricKeys.map((key) => [key, 0])) as SessionAggregate['coverage']
export const SESSION_ACCOUNTING_VERSION = 10
const HOUR_MS = 3_600_000

export class SessionEngine {
  private record: SessionRecord | null = null
  private pauseStartedAt: number | null = null
  private accumulatedPauseMs = 0
  private latestState: GameStateSnapshot | null = null
  private captureGeneration = 0
  private readonly ledger: TrustedMetricLedger
  private readonly expCounter: SessionExpCounter
  private readonly expRateTracker = new SessionExpRateTracker()
  private readonly expReadout = new SessionExpReadout()
  private readonly journeyRecorder = new SessionJourneyRecorder()
  private readonly rankTracker: EfficiencySegmentTracker | null
  private pendingRankSubmissions: RankSubmissionDraft[] = []
  private latestAccounting: AccountingFrame | null = null
  private readonly confirmedLosses = new Set<string>()
  private rankPerformanceRestricted = false
  private rankSourceRestricted: RankSourceRestriction | null = null

  constructor(private readonly preset: GamePreset) {
    this.ledger = new TrustedMetricLedger(preset)
    this.expCounter = new SessionExpCounter((display, invalidated) => {
      this.expRateTracker.ingest(display, this.activeDuration(display.sourceAt ?? 0), invalidated)
    })
    this.rankTracker = preset.id === RANK_PRESET_ID ? new EfficiencySegmentTracker(preset.version) : null
  }

  start(
    profileId: string,
    now = Date.now(),
    initialState: GameStateSnapshot | null = null,
    captureGeneration = 0,
  ): SessionRecord {
    if (this.record && this.record.status !== 'ended') throw new Error('A record is already active')
    this.pauseStartedAt = null
    this.accumulatedPauseMs = 0
    this.latestState = initialState
    this.latestAccounting = null
    this.captureGeneration = captureGeneration
    this.pendingRankSubmissions = []
    this.confirmedLosses.clear()
    this.rankPerformanceRestricted = false
    this.rankSourceRestricted = null
    this.rankTracker?.reset()
    this.ledger.reset(now, captureGeneration, initialState)
    this.expRateTracker.reset(now)
    this.record = {
      id: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      profileId,
      status: 'running',
      startedAt: now,
      activeDurationMs: 0,
      sessionAccountingVersion: SESSION_ACCOUNTING_VERSION,
      journeyVersion: SESSION_JOURNEY_VERSION,
      aggregate: this.emptyAggregate(),
      ...(initialState ? { startState: initialState, endState: initialState } : {}),
    }
    this.expCounter.reset(initialState, now)
    this.expReadout.reset()
    if (initialState) this.expReadout.ingest(this.expCounter, initialState, undefined, [], .62, at => this.activeDuration(at))
    const baseline = this.expCounter.snapshot()
    if (initialState && baseline.sourceAt !== null) this.expRateTracker.ingest({ ...baseline, sourceAt: now }, 0)
    const record = this.update({}, now)
    this.journeyRecorder.start(record, initialState)
    return record
  }

  pause(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset(now)
    this.rankTracker?.suspend()
    const record = this.update({ status: 'paused', pausedAt: now }, now)
    this.journeyRecorder.mark('pause', record, now)
    return record
  }

  resume(now = Date.now(), captureGeneration = this.captureGeneration + 1): SessionRecord {
    this.assertStatus('paused', 'interrupted')
    const previousStatus = this.record!.status
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.captureGeneration = captureGeneration
    this.ledger.resume(now, captureGeneration)
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset(now)
    const record = this.update({ status: 'running', pausedAt: undefined }, now)
    this.journeyRecorder.mark(previousStatus === 'interrupted' ? 'capture-restored' : 'resume', record, now)
    return record
  }

  interrupt(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.ledger.suspend()
    this.expCounter.breakContinuity(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset(now)
    this.rankTracker?.suspend()
    const record = this.update({ status: 'interrupted', pausedAt: now }, now)
    this.journeyRecorder.mark('capture-interrupted', record, now)
    return record
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
  ): SessionRecord {
    return this.ingestWithAccounting(state, events, observation, captureGeneration).record
  }

  reconnectCapture(now: number, captureGeneration: number): SessionRecord {
    this.assertStatus('running')
    this.expCounter.reconnectCapture(now)
    this.expReadout.breakContinuity(now)
    this.expRateTracker.reset(now)
    this.journeyRecorder.breakContinuity()
    // The trusted ledger still sees the new generation and excludes unobserved intervals.
    this.captureGeneration = captureGeneration
    return this.update({}, now)
  }

  ingestWithAccounting(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation?: ValidatedObservation,
    captureGeneration = this.captureGeneration,
    minimumConfidence = .62,
  ): SessionIngestResult {
    this.assertStatus('running')
    if (this.captureGeneration !== captureGeneration) {
      this.expCounter.invalidateContinuity(state.capturedAt)
      this.expReadout.breakContinuity(state.capturedAt)
      this.expRateTracker.reset(state.capturedAt)
    }
    this.captureGeneration = captureGeneration
    const activeAt = this.activeDuration(state.capturedAt)
    const accounting = this.ledger.ingest(state, events, observation, activeAt, captureGeneration)
    if (observation) this.expCounter.ingestObservation(observation, minimumConfidence)
    else this.expCounter.ingestState(state)
    this.expCounter.confirmLevelUp(events)
    this.expReadout.ingest(this.expCounter, state, observation, events, minimumConfidence, at => this.activeDuration(at))
    const change = this.expCounter.snapshot().journeyTransition
    if (change?.id && change.status === 'confirmed') {
      if (change.kind === 'loss') this.confirmedLosses.add(change.id)
      else if (change.kind === 'level-up' && (change.revision ?? 0) > 1) this.confirmedLosses.delete(change.id)
    }
    if (this.confirmedLosses.size) {
      if (!this.record!.rankIneligibleReason) this.record = { ...this.record!, rankIneligibleReason: 'confirmed-exp-loss' }
      this.pendingRankSubmissions = []
      this.rankTracker?.reset()
    } else if (this.record!.rankIneligibleReason === 'confirmed-exp-loss') {
      this.record = { ...this.record!, rankIneligibleReason: this.rankSourceRestricted ?? (this.rankPerformanceRestricted ? 'low-performance-tier' : undefined) }
      this.rankTracker?.reset()
    }
    this.latestAccounting = accounting
    const startState = this.record!.startState ?? state
    this.latestState = state
    const record = this.update({ startState, endState: state }, state.capturedAt)
    this.journeyRecorder.ingest(record, accounting, events, state, this.expCounter.snapshot())
    const completedRankSegment = record.rankIneligibleReason ? null : this.rankTracker?.ingest(state, accounting)
    if (completedRankSegment) this.pendingRankSubmissions.push(completedRankSegment)
    return { record, accounting }
  }

  end(now = Date.now()): SessionRecord {
    this.assertStatus('running', 'paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.expCounter.closePending(now)
    this.expRateTracker.reset(now)
    const record = this.update({ status: 'ended', endedAt: now, pausedAt: undefined }, now)
    this.journeyRecorder.finish(record, now)
    const completedRankSegment = record.rankIneligibleReason ? null : this.rankTracker?.finish(now)
    if (completedRankSegment) this.pendingRankSubmissions.push(completedRankSegment)
    return record
  }

  current(now = Date.now()): SessionRecord | null {
    if (!this.record) return null
    this.expCounter.expireJourney(now)
    this.journeyRecorder.expirePending(now)
    return this.update({}, now)
  }

  liveRates(now = this.latestState?.capturedAt ?? Date.now()): LiveRateSnapshot {
    this.expCounter.expireJourney(now)
    const trusted = this.ledger.liveRates(now)
    const durationMs = this.currentActiveDuration(now)
    const gained = this.expCounter.total()
    return {
      ...trusted,
      ...this.expReadout.snapshot(this.expCounter, now, this.record?.status === 'running', durationMs),
      expRealtime: this.expRateTracker.snapshot(now, this.expPerHour(gained, durationMs),
        this.expCounter.snapshot().status, this.record?.status === 'running'),
      expPerHour: {
        value: this.expPerHour(gained, durationMs),
        calculationStatus: this.expCounter.snapshot().status,
        approximate: this.expCounter.approximate(),
        status: gained === null ? 'unavailable' : 'ready',
        activeDurationMs: durationMs,
        updatedAt: this.expCounter.latestSourceAt(),
      },
    }
  }

  accountingFrame(): AccountingFrame | null {
    return this.latestAccounting
  }

  expDisplayStatus() {
    return this.expCounter.snapshot()
  }

  journey(): SessionJourney | null {
    return this.journeyRecorder.snapshot()
  }

  restrictRankForPerformance(tier: PerformanceTier, captureFps: number | null = null): SessionRecord | null {
    if (!this.record || this.record.status === 'ended') return this.record
    // A low-rate shared stream remains low-rate until the next share, even after changing tiers.
    if (tier !== 'low' && tier !== 'medium' && !(captureFps !== null && captureFps > 0 && captureFps < 1)) return this.record
    this.rankPerformanceRestricted = true
    this.record = { ...this.record, rankIneligibleReason: this.rankSourceRestricted ?? 'low-performance-tier' }
    this.pendingRankSubmissions = []
    this.rankTracker?.reset()
    return this.record
  }

  restrictRankForSource(reason: RankSourceRestriction | null): SessionRecord | null {
    if (!reason || !this.record || this.record.status === 'ended') return this.record
    this.rankSourceRestricted ??= reason
    this.record = { ...this.record, rankIneligibleReason: this.rankSourceRestricted }
    this.pendingRankSubmissions = []
    this.rankTracker?.reset()
    return this.record
  }

  setExpSamplingInterval(intervalMs: number): void {
    this.expRateTracker.setSamplingInterval(intervalMs)
  }

  setExpReadoutInterval(intervalMs: number): void {
    this.expReadout.setSamplingInterval(intervalMs)
  }

  rankSubmissions(): readonly RankSubmissionDraft[] {
    return this.record?.rankIneligibleReason ? [] : [...this.pendingRankSubmissions]
  }

  private update(patch: Partial<SessionRecord>, now: number): SessionRecord {
    if (!this.record) throw new Error('No active record')
    const activeDurationMs = this.currentActiveDuration(now)
    const next = { ...this.record, ...patch, activeDurationMs }
    this.record = { ...next, aggregate: this.aggregate(next, activeDurationMs) }
    return this.record
  }

  private activeDuration(now: number): number {
    if (!this.record) return 0
    const effectiveNow = this.record.status === 'ended' && this.record.endedAt !== undefined
      ? Math.min(now, this.record.endedAt)
      : now
    const currentPause = this.pauseStartedAt === null ? 0 : Math.max(0, effectiveNow - this.pauseStartedAt)
    return Math.max(0, effectiveNow - this.record.startedAt - this.accumulatedPauseMs - currentPause)
  }

  private currentActiveDuration(now: number): number {
    return Math.max(this.record?.activeDurationMs ?? 0, this.activeDuration(now))
  }

  private aggregate(record: SessionRecord, durationMs: number): SessionAggregate {
    const end = this.latestState ?? record.endState
    const trustedAggregate = this.ledger.aggregate()
    const expGained = this.expCounter.total()
    return {
      durationMs,
      ...trustedAggregate,
      expGained,
      expDisplayStatus: this.expCounter.snapshot().status,
      expGainedApproximate: this.expCounter.approximate(),
      expRateGained: trustedAggregate.expGained,
      expPerHour: this.expPerHour(expGained, durationMs),
      coverage: end?.coverage ?? emptyCoverage(),
    }
  }

  private emptyAggregate(): SessionAggregate {
    return {
      durationMs: 0,
      expGained: null,
      expRateGained: null,
      expTrustedDurationMs: 0,
      expTrustedPerHour: null,
      expPerHour: null,
      mesosStartBalance: null,
      mesosCurrentBalance: null,
      mesosBalanceChange: null,
      mesosDelta: null,
      mesosTrustedDurationMs: 0,
      mesosPerHour: null,
      coverage: emptyCoverage(),
    }
  }

  private expPerHour(gained: number | null, durationMs: number): number | null {
    if (gained === null) return null
    const value = durationMs > 0 ? gained / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : null
  }

  private assertStatus(...allowed: SessionRecord['status'][]): void {
    if (!this.record || !allowed.includes(this.record.status)) throw new Error(`Invalid record transition from ${this.record?.status ?? 'none'}`)
  }
}
